import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	StocktakingApi,
	StocktakingDraftStateApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";

interface Props {
	isShown: boolean;
	onHide: () => void;
	stocktaking: StocktakingApi;
	draftId: number;
	handleSuccessAction: () => void;
}

const AddProductModalForm = ({ isShown, onHide, draftId, stocktaking, handleSuccessAction }: Props) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(false);
	const form = useForm<StocktakingDraftStateApi>({
		criteriaMode: "all",
	});
	const {
		formState,
		formState: { errors },
		handleSubmit,
		control,
		setError,
	} = form;
	const isValueStocktakingType = stocktaking.type === "VALUE";
	const organization = useSelector(selectOrganization);
	const currency = organization.currency || "PLN";
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const parseFormData = (data: StocktakingDraftStateApi) => {
		if (data.value?.amount !== undefined) {
			data.value = {
				...data.value,
				currency,
			};
		}
		return data;
	};

	const onSubmit = handleSubmit(async (data: StocktakingDraftStateApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const newData = parseFormData(data);
			await api.organization().createStocktakingDraftState(stocktaking.id, draftId, newData);
			addSuccessFlash(t("lib:common.flash.saved"));
			handleSuccessAction();
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const searchProduct = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const productIdValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const volumeValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const amountValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const valueAmountValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	return (
		<Modal show={isShown} onHide={onHide}>
			<FormDirty formState={formState} key="add-product-form">
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.stocktaking_draft.field.add_product.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormSelectGroup
						control={control}
						loadOptions={searchProduct}
						label={t("common.word.product")}
						getOptionLabel={(option) => {
							return option.product_type === "PACKAGE" ? (
								<PackageSearchSelectListItem label={option.label} />
							) : (
								option.label
							);
						}}
						name="product_id"
						errors={errors}
						getOptionValue={(opt) => opt.id}
						customValidationConfig={productIdValidationConfig}
						data-testid="product_id"
					/>
					<div className="row">
						<div className={`${isValueStocktakingType ? "col-md-4" : "col-md-6"}`}>
							<FormNumberInput
								errors={errors}
								name="volume"
								control={control}
								suffix=""
								label={t("common.word.volume")}
								customValidationConfig={volumeValidationConfig}
							/>
						</div>
						<div className={`${isValueStocktakingType ? "col-md-4" : "col-md-6"}`}>
							<FormNumberInput
								errors={errors}
								name="amount"
								control={control}
								suffix=""
								label={t("common.word.quantity")}
								customValidationConfig={amountValidationConfig}
							/>
						</div>
						{isValueStocktakingType && (
							<div className="col-md-4">
								<FormMoneyInput
									errors={errors}
									name="value.amount"
									control={control}
									currency=""
									withoutCurrency
									label={t("common.word.value")}
									customValidationConfig={valueAmountValidationConfig}
								/>
							</div>
						)}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.add")}
					</ButtonLoading>
					<Button onClick={onHide} variant="light">
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default AddProductModalForm;
